import { BadRequestException, Injectable, Logger, NotFoundException } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { AuditService } from '../audit/audit.service';
import { EmailService } from '../notifications/email.service';
import { PLAN_QUOTA_BYTES, planQuotaBytes, resolvePlanTier } from '../commerce-admin/plan-quota.constants';
import { MediaBackupService } from './media-backup.service';
import { MediaReconciliationService } from './media-reconciliation.service';
import { StorageConfigService } from './storage-config.service';
import { StorageHealthService } from './storage-health.service';
import { StorageQuotaService, warningLevelFor } from './storage-quota.service';
import { countsAgainstQuota, isByos } from './platform-storage.constants';

/** R2's published rate, used for the fleet cost estimate only. */
const USD_PER_GB_MONTH = 0.015;

@Injectable()
export class PlatformStorageAdminService {
  private readonly logger = new Logger(PlatformStorageAdminService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly quota: StorageQuotaService,
    private readonly configs: StorageConfigService,
    private readonly health: StorageHealthService,
    private readonly reconciliation: MediaReconciliationService,
    private readonly backups: MediaBackupService,
    private readonly audit?: AuditService,
    private readonly email?: EmailService
  ) {}

  async fleetOverview() {
    const [totals, byMode, healthStates, retainedHeavy, orgCount] = await Promise.all([
      this.prisma.mediaAsset.aggregate({
        where: { status: { in: ['reserved', 'active', 'trashed'] } },
        _sum: { sizeBytes: true },
        _count: { _all: true },
      }),
      this.prisma.merchantStorageConfig.groupBy({
        by: ['mode'],
        where: { status: { in: ['active', 'retained'] } },
        _sum: { assetBytes: true },
        _count: { _all: true },
      }),
      this.health.fleetHealth(),
      this.health.orgsWithManyRetainedConfigs(3),
      this.prisma.organization.count({ where: { deletedAt: null } }),
    ]);

    const platformBytes = byMode
      .filter(m => countsAgainstQuota(m.mode))
      .reduce((sum, m) => sum + Number(m._sum.assetBytes ?? 0), 0);
    const byosBytes = byMode
      .filter(m => isByos(m.mode))
      .reduce((sum, m) => sum + Number(m._sum.assetBytes ?? 0), 0);

    const [nearQuota, failedPurges, lastDrillNote] = await Promise.all([
      this.merchantsNearQuota(),
      this.prisma.mediaAsset.count({ where: { status: 'purge_failed' } }),
      this.prisma.mediaBackupRun.findFirst({ orderBy: { startedAt: 'desc' } }),
    ]);

    return {
      totalBytes: Number(totals._sum.sizeBytes ?? 0),
      totalAssets: totals._count._all,
      organizations: orgCount,
      byMode: byMode.map(m => ({
        mode: m.mode,
        bytes: Number(m._sum.assetBytes ?? 0),
        backends: m._count._all,
      })),
      platformBytes,
      byosBytes,
      // Only platform-managed bytes are ours to pay for; BYOS is billed to the
      // merchant by their own provider.
      estimatedMonthlyCostUsd: Number(((platformBytes / 1024 ** 3) * USD_PER_GB_MONTH).toFixed(2)),
      health: healthStates,
      merchantsNearQuota: nearQuota,
      failedPurges,
      orgsWithManyRetainedBackends: retainedHeavy,
      backupConfigured: this.backups.isConfigured(),
      lastBackupRun: lastDrillNote,
    };
  }

  private async merchantsNearQuota(thresholdPercent = 80) {
    const orgs = await this.prisma.organization.findMany({
      where: { deletedAt: null, storageUsedBytes: { gt: 0 } },
      select: { id: true, name: true, plan: true, storageCapBytes: true, storageUsedBytes: true },
    });

    return orgs
      .map(org => {
        const cap = org.storageCapBytes != null ? Number(org.storageCapBytes) : planQuotaBytes(org.plan);
        const used = Number(org.storageUsedBytes);
        const percent = cap > 0 ? (used / cap) * 100 : 0;
        return {
          organizationId: org.id,
          name: org.name,
          plan: org.plan,
          usedBytes: used,
          capBytes: cap,
          usagePercent: Number(percent.toFixed(1)),
          warningLevel: warningLevelFor(percent),
        };
      })
      .filter(o => o.usagePercent >= thresholdPercent)
      .sort((a, b) => b.usagePercent - a.usagePercent);
  }

  async merchantUsage(limit = 50) {
    const orgs = await this.prisma.organization.findMany({
      where: { deletedAt: null },
      select: {
        id: true,
        name: true,
        plan: true,
        byosEnabled: true,
        storageCapBytes: true,
        storageUsedBytes: true,
        storageAssetCount: true,
      },
      orderBy: { storageUsedBytes: 'desc' },
      take: limit,
    });

    return orgs.map(org => {
      const cap = org.storageCapBytes != null ? Number(org.storageCapBytes) : planQuotaBytes(org.plan);
      const used = Number(org.storageUsedBytes);
      return {
        organizationId: org.id,
        name: org.name,
        plan: org.plan,
        planTier: resolvePlanTier(org.plan),
        byosEnabled: org.byosEnabled,
        usedBytes: used,
        capBytes: cap,
        assetCount: org.storageAssetCount,
        usagePercent: cap > 0 ? Number(((used / cap) * 100).toFixed(1)) : 0,
        hasCustomCap: org.storageCapBytes != null,
      };
    });
  }

  /**
   * A merchant's backends as an admin may see them: provider, health, counts.
   *
   * Bucket name, endpoint and key id are deliberately absent — an R2 endpoint
   * embeds the merchant's Cloudflare account id, and none of it is needed to
   * answer the questions an admin actually has ("is it connected", "how much
   * is on it", "when did it last verify").
   */
  async merchantStorage(organizationId: string) {
    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: { id: true, name: true, plan: true, byosEnabled: true, storageCapBytes: true, storageUsedBytes: true },
    });
    if (!org) throw new NotFoundException(`Merchant ${organizationId} not found`);

    const configs = await this.prisma.merchantStorageConfig.findMany({
      where: { organizationId, status: { not: 'archived' } },
      select: {
        id: true,
        mode: true,
        status: true,
        healthState: true,
        healthMessage: true,
        lastVerifiedAt: true,
        assetCount: true,
        assetBytes: true,
        createdAt: true,
      },
      orderBy: { createdAt: 'desc' },
    });

    return {
      organizationId: org.id,
      name: org.name,
      plan: org.plan,
      byosEnabled: org.byosEnabled,
      capBytes: org.storageCapBytes != null ? Number(org.storageCapBytes) : planQuotaBytes(org.plan),
      usedBytes: Number(org.storageUsedBytes),
      backends: configs.map(c => ({
        id: c.id,
        mode: c.mode,
        status: c.status,
        healthState: c.healthState,
        healthMessage: c.healthMessage,
        lastVerifiedAt: c.lastVerifiedAt,
        assetCount: c.assetCount,
        assetBytes: Number(c.assetBytes),
        createdAt: c.createdAt,
        // Present so the UI can render a button; the reveal itself is audited.
        identityHidden: true,
      })),
    };
  }

  async setQuota(
    organizationId: string,
    input: { plan?: string; storageCapBytes?: number | null },
    actorId: string,
    reason?: string
  ) {
    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: { plan: true, storageCapBytes: true },
    });
    if (!org) throw new NotFoundException(`Merchant ${organizationId} not found`);

    if (input.plan !== undefined && !Object.keys(PLAN_QUOTA_BYTES).includes(input.plan)) {
      throw new BadRequestException(`Unknown plan "${input.plan}"`);
    }
    if (input.storageCapBytes !== undefined && input.storageCapBytes !== null && input.storageCapBytes < 0) {
      throw new BadRequestException('storageCapBytes cannot be negative');
    }

    const before = {
      plan: org.plan,
      storageCapBytes: org.storageCapBytes == null ? null : Number(org.storageCapBytes),
    };

    await this.prisma.organization.update({
      where: { id: organizationId },
      data: {
        ...(input.plan !== undefined ? { plan: input.plan } : {}),
        ...(input.storageCapBytes !== undefined
          ? { storageCapBytes: input.storageCapBytes === null ? null : BigInt(input.storageCapBytes) }
          : {}),
        updatedBy: actorId,
      },
    });

    const after = {
      plan: input.plan ?? before.plan,
      storageCapBytes: input.storageCapBytes !== undefined ? input.storageCapBytes : before.storageCapBytes,
    };

    // Before/after in one record: plan and quota changes are the ones a
    // merchant disputes months later, and "we raised it when you paid" needs
    // to be checkable rather than remembered.
    await this.audit?.logChange({
      schemaName: 'platform',
      tableName: 'organizations',
      recordId: organizationId,
      operation: 'update',
      changedColumns: ['plan', 'storageCapBytes'],
      oldValue: before,
      newValue: { ...after, reason: reason ?? null },
      changedBy: actorId,
      source: 'storage-admin',
    });

    return { success: true, before, after };
  }

  async setByosEnabled(organizationId: string, enabled: boolean, actorId: string) {
    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: { byosEnabled: true },
    });
    if (!org) throw new NotFoundException(`Merchant ${organizationId} not found`);

    await this.prisma.organization.update({
      where: { id: organizationId },
      data: { byosEnabled: enabled, updatedBy: actorId },
    });

    await this.audit?.logChange({
      schemaName: 'platform',
      tableName: 'organizations',
      recordId: organizationId,
      operation: 'update',
      changedColumns: ['byosEnabled'],
      oldValue: { byosEnabled: org.byosEnabled },
      newValue: { byosEnabled: enabled },
      changedBy: actorId,
      source: 'storage-admin',
    });

    return { success: true, byosEnabled: enabled };
  }

  async recomputeUsage(organizationId: string, actorId: string) {
    const result = await this.quota.recompute(organizationId);
    if (result.before !== result.after) {
      this.logger.warn(
        `Usage drift repaired for org ${organizationId}: ${result.before} → ${result.after} bytes (by ${actorId})`
      );
    }
    return result;
  }

  async forcePlatformStorage(organizationId: string, actorId: string) {
    const config = await this.configs.usePlatformStorage(organizationId, actorId);
    await this.audit?.logChange({
      schemaName: 'platform',
      tableName: 'merchant_storage_configs',
      recordId: config.id,
      operation: 'update',
      newValue: { organizationId, event: 'forced_platform_storage', by: actorId },
      changedBy: actorId,
      source: 'storage-admin',
    });
    return config;
  }

  /**
   * Reveals bucket identity, never credentials.
   *
   * Three things happen on every call and none of them are optional: the
   * justification is recorded, the audit row names the admin, and the merchant
   * is emailed. That combination is what makes "our admins cannot look at your
   * storage config without you knowing" a checkable claim rather than a
   * promise.
   */
  async breakGlassReveal(organizationId: string, configId: string, justification: string, actorId: string) {
    const config = await this.prisma.merchantStorageConfig.findFirst({
      where: { id: configId, organizationId },
      select: {
        id: true,
        mode: true,
        bucket: true,
        region: true,
        publicBaseUrl: true,
        accessKeyIdMasked: true,
        healthState: true,
      },
    });
    if (!config) throw new NotFoundException(`Storage config ${configId} not found for this merchant`);

    await this.audit?.logChange({
      schemaName: 'platform',
      tableName: 'merchant_storage_configs',
      recordId: configId,
      operation: 'read',
      newValue: {
        event: 'break_glass_reveal',
        organizationId,
        revealedBy: actorId,
        justification,
        // The endpoint stays hidden even here: it embeds the merchant's
        // provider account id, and nothing about supporting them needs it.
        revealed: ['bucket', 'region', 'publicBaseUrl', 'accessKeyIdMasked'],
      },
      changedBy: actorId,
      source: 'storage-admin',
    });

    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: { name: true, email: true },
    });
    if (org?.email) {
      await this.email
        ?.sendTemplated('storage_config_revealed', org.email, {
          customerName: org.name,
          businessName: org.name,
          adminId: actorId,
          justification,
          revealedAt: new Date().toISOString(),
        })
        .catch((err: unknown) => this.logger.warn(`Reveal notification failed: ${err}`));
    }

    return {
      id: config.id,
      mode: config.mode,
      bucket: config.bucket,
      region: config.region,
      publicBaseUrl: config.publicBaseUrl,
      accessKeyIdMasked: config.accessKeyIdMasked,
      healthState: config.healthState,
      // Stated in the payload so nobody goes looking for a "full reveal"
      // endpoint: there is none, and the secret has no read path in the code.
      secretAccessKey: 'never-revealed',
      merchantNotified: Boolean(org?.email),
    };
  }

  reconcile(configId: string) {
    return this.reconciliation.reconcileConfig(configId);
  }

  restoreDrill() {
    return this.backups.restoreDrill();
  }
}
